import { useFeatureIsOn, useGrowthBook } from '@growthbook/growthbook-react';
import { GROWTHBOOK_FEATURE_FLAGS } from '@web-app/constants/growthbook';
import { GrowthBookAttributes } from '@web-app/types/growthbook';
import { useEffect } from 'react';

/**
 * Sets the `appointmentTypeId` attribute on the GrowthBook instance for the
 * duration this hook is mounted, and clears it on unmount so it does not leak
 * into flag evaluations outside the call view.
 */
export const useSyncAppointmentTypeIdAttribute = (
  appointmentTypeId: string | null | undefined,
): void => {
  const growthbook = useGrowthBook();

  useEffect(() => {
    if (!growthbook || !appointmentTypeId) {
      return;
    }

    const next: GrowthBookAttributes = {
      ...growthbook.getAttributes(),
      appointmentTypeId,
    };
    growthbook.setAttributes(next);

    return () => {
      const cleared: GrowthBookAttributes = {
        ...growthbook.getAttributes(),
        appointmentTypeId: undefined,
      };
      growthbook.setAttributes(cleared);
    };
  }, [growthbook, appointmentTypeId]);
};

/**
 * Sets the `isMedicalDoctor` attribute on the GrowthBook instance whenever a
 * defined value is provided. Pass `undefined` while the underlying data is
 * still loading so the attribute is not set with a placeholder.
 *
 * No cleanup on unmount: this is a session-wide provider attribute mounted
 * once at the app root.
 */
export const useSyncIsMedicalDoctorAttribute = (
  isMedicalDoctor: boolean | null | undefined,
): void => {
  const growthbook = useGrowthBook();

  useEffect(() => {
    if (
      !growthbook ||
      isMedicalDoctor === null ||
      isMedicalDoctor === undefined
    ) {
      return;
    }

    const next: GrowthBookAttributes = {
      ...growthbook.getAttributes(),
      isMedicalDoctor,
    };
    growthbook.setAttributes(next);
  }, [growthbook, isMedicalDoctor]);
};

/**
 * Returns whether the current user/appointment is in the "prescriber flow"
 * (e.g., MD provider running a GLP-1 visit). Targeting is configured on the
 * `is-prescriber-flow` flag in GrowthBook against the `appointmentTypeId` and
 * `isMedicalDoctor` attributes.
 *
 * Use this flag to hide non-prescriber UI (e.g., Journal tab) or show
 * prescriber-only UI inside the call view.
 */
export const useIsPrescriberFlow = (): boolean => {
  return useFeatureIsOn(GROWTHBOOK_FEATURE_FLAGS.IS_PRESCRIBER_FLOW);
};
